import React, { useEffect, useState } from 'react';
import { PageContainer } from '../components/layout/page';
import { Table } from '../components/ui/tables';
import { HeroImage, TalentImage } from '../components/ui/file-contents';
import { Places } from '../components/ui/other';
import { isSubActive, sortByCondition } from '../../common/utils';
import { SubOutdated } from '../components/layout/blocks';
import { SelectInput, SearchInput } from '../components/functional/inputs';
import { ToggleSwitch } from '../components/functional/buttons';
import { TableHeaderColSort } from '../components/ui/models/tables';
import { AuthorizedUser } from '../../api/repositories/backend/models/auth';
import { HeroesRepository } from '../../api/repositories/stats/heroes';
import { HeroesMetaPeriod, HeroesMetaResponse } from '../../api/repositories/stats/models/heroes';
import { SubscribeGlobalState } from '../../common/hooks/global-state';
import { localize } from '../../common/services/localization';

export const Meta: React.FC = () => {
    const authorizedUser = SubscribeGlobalState('authorizedUser') as AuthorizedUser | undefined;
    const [subIsActive, setSubIsActive] = useState<boolean | null>(null);

    useEffect(() => {
        const isActive = isSubActive(authorizedUser);
        setSubIsActive(isActive);
    }, [authorizedUser]);

    if (subIsActive === null) {
        return null;
    }

    return (
        <PageContainer
            containerClassName="min-h-[600px] xl:min-h-[350px] flex flex-col justify-center items-center"
            headerImgPath="/v2/images/bg/dragon.png"
            child={
                <div className="mb-[60px] max-w-[1033px]">
                    <h2 className="mb-4 uppercase">{localize('meta_title')}</h2>
                    {subIsActive
                        ? <MetaContainer />
                        : <SubOutdated authorizedUser={authorizedUser} withBtn={true} />
                    }
                </div>
            }
        />
    );
};

const MetaContainer: React.FC = () => {
    const heroes = SubscribeGlobalState('heroes')

    const repository = new HeroesRepository();

    const [period, setPeriod] = useState<HeroesMetaPeriod>('four_days');
    const [withTalents, setWithTalents] = useState<boolean>(false);

    const [searchValue, setSearchValue] = useState<string | undefined>();
    const [sortCondition, setSortCondition] = useState<TableHeaderColSort | undefined>();

    const [response, setResponse] = useState<ApiDataResponse<HeroesMetaResponse> | undefined>();

    useEffect(() => {
        loadValues(period, withTalents);
    }, []);

    function loadValues(period: HeroesMetaPeriod, withTalents: boolean) {
        repository.getMeta({ period, withTalents })
            .then(v => setResponse(v));
    }

    function updatePeriodValue(value?: string) {
        setPeriod(value as HeroesMetaPeriod);
        loadValues(value as HeroesMetaPeriod, withTalents);
    }

    function updateWithTalents(value: boolean) {
        setWithTalents(value);
        loadValues(period, value);
    }

    function updateSearchValue(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        const condition = value.trim();
        setSearchValue(condition.length > 0 ? condition.toLowerCase() : undefined);
    }

    const colsValues = response?.data?.heroes
        .filter(hero => {
            if (!searchValue)
                return true;

            const fullHero = heroes?.find(f => f.name == hero.heroName);

            if (fullHero) {
                return fullHero.userFriendlyName.toLowerCase().includes(searchValue);
            }

            return true;
        })
        .sort((a, b,) => sortByCondition(a, b, sortCondition));

    const options: { localName: string; value: HeroesMetaPeriod }[] = [
        {
            localName: 'meta_period_last_4_days',
            value: 'four_days'
        },
        {
            localName: 'meta_period_past_week',
            value: 'prev_week'
        }
    ];

    return (
        <div className="lg:mt-[60px]">
            <div className="flex flex-col-reverse items-center lg:flex-row justify-center gap-[16px] lg:gap-[] lg:justify-between ">
                <SearchInput className='w-screen lg:w-full' placeholderLocal="search_hero_placeholder" onChange={updateSearchValue} />
                <SelectInput className='w-screen lg:w-full' onChange={updatePeriodValue} options={options} />
                <div className='w-[127px] h-[52px] flex items-center gap-[12px]'>
                    <span className="font-['Roboto'] text-[18px]">
                        {localize('meta_talents')}
                    </span>
                    <ToggleSwitch
                        isOn={withTalents}
                        onToggle={updateWithTalents}
                    />
                </div>
            </div>
            <div className='w-full items-center mx-auto'>
                <Table
                    tableClassName=''
                    className="mt-[42px]"
                    needSeparate={true}
                    cols={[
                        {
                            headLocalName: "Hero",
                            className: "w-[40px] lg:w-[140px] text-left lg:pl-[72px]"
                        },
                        {
                            headLocalName: "Pickrate",
                            className: "w-[40px] lg:w-[100px] text-center",
                            sort: sortCondition?.key === 'pickrate' ? sortCondition : undefined,
                            onSortChange(direction) {
                                setSortCondition({ key: 'pickrate', direction: direction });
                            },
                        },
                        {
                            headLocalName: "Total",
                            className: "w-[40px] lg:w-[100px] text-center",
                            sort: sortCondition?.key === 'matchCount' ? sortCondition : undefined,
                            onSortChange(direction) {
                                setSortCondition({ key: 'matchCount', direction: direction });
                            },
                        },
                        {
                            headLocalName: "Winrate",
                            className: "w-[40px] lg:w-[100px] text-center",
                            sort: sortCondition?.key === 'averagePlace' ? sortCondition : undefined,
                            onSortChange(direction) {
                                setSortCondition({ key: 'averagePlace', direction: direction });
                            },
                        },
                        {
                            headLocalName: "Places",
                            className: "w-[40px] lg:w-[164px] text-center"
                        },
                    ]}
                    rows={
                        colsValues
                            ? colsValues.map((hero) => ({
                                href: `/heroes/${hero.urlName}`,
                                cols: [
                                    {
                                        value: withTalents
                                            ? <div className="relative w-fit h-fit">
                                                <HeroImage className='' heroName={hero.heroName} size={'136x78'} />
                                                <div className="absolute top-0 right-0 2xl:top-[15px] 2xl:right-[-24px]">
                                                    <TalentImage className='h-[35px] 2xl:h-full border-orange' name={hero.talentName!} size={'47x47'} />
                                                </div>
                                            </div>
                                            : <HeroImage className='h-[40px] md:h-[60px] 2xl:h-full' heroName={hero.heroName} size={'136x78'} />,
                                        className: 'lg:pl-[54px] lg:px-[16px]'
                                    },
                                    {
                                        value: hero.pickrate
                                            ? `${(hero.pickrate * 100).toFixed(1)}%`
                                            : '—',
                                        className: 'w-[40px] lg:w-[100px] text-center shadow-text-gold'
                                    },
                                    {
                                        value: hero.matchCount ?? '—',
                                        className: 'w-[40px] lg:w-[100px] text-center shadow-text-gold'
                                    },
                                    {
                                        value: hero.averagePlace?.toFixed(2) ?? '-',
                                        className: 'w-[40px] lg:w-[100px] text-center shadow-text-gold'
                                    },
                                    {
                                        value: <Places places={hero.places} />,
                                        className: 'w-[40px] lg:w-[100px]'
                                    },
                                ],
                            }))
                            : MetaEmptyRows()
                    }
                />
            </div>
            {
                colsValues?.length
                    ? <></>
                    : <h3 className='mt-[60px] text-center'>
                        Не найдено
                    </h3>
            }
        </div>
    );
};

const MetaEmptyRows = () => {
    const retval = [];

    for (let index = 0; index < 100; index++) {
        retval.push(MetaEmptyRow());
    }

    return retval;
}

const MetaEmptyRow = () => {
    return {
        cols: [
            {
                value: '',
                className: 'lg:w-[140px] h-[110px]'
            },
            {
                value: '',
                className: 'lg:w-[100px] text-center shadow-text-gold'
            },
            {
                value: '',
                className: 'lg:w-[100px] text-center shadow-text-gold'
            },
            {
                value: '',
                className: 'lg:w-[100px] text-center shadow-text-gold'
            },
            {
                value: '',
                className: 'lg:w-[164px]'
            },
        ],
    }
}
